import { useCallback, useState } from 'react';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';

export type EmailChangeStep = 'idle' | 'enterNewEmail' | 'enterCode' | 'success';

const RESEND_COOLDOWN_MS = 30_000;

function readTrpcErrorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return null;
}

/** The two-step email change: users.requestEmailChange, then users.confirmEmailChange. */
export function useEmailChange(onChanged: () => Promise<unknown>) {
  const trpcClient = useTRPCClientAPI();
  const [step, setStep] = useState<EmailChangeStep>('idle');
  const [newEmail, setNewEmail] = useState('');
  const [code, setCode] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const requestMutation = useTRPCMutation({
    mutationFn: (email: string) => trpcClient.users.requestEmailChange.mutate({ newEmail: email }),
    onSuccess: () => {
      setStep('enterCode');
      setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
      setErrorCode(null);
    },
    onError: (err) => setErrorCode(readTrpcErrorCode(err)),
  });

  const confirmMutation = useTRPCMutation({
    mutationFn: (codeValue: string) => trpcClient.users.confirmEmailChange.mutate({ code: codeValue }),
    onSuccess: async () => {
      setErrorCode(null);
      setStep('success');
      await onChanged();
    },
    onError: (err) => setErrorCode(readTrpcErrorCode(err)),
  });

  const start = useCallback(() => {
    setStep('enterNewEmail');
    setNewEmail('');
    setCode('');
    setErrorCode(null);
  }, []);

  const cancel = useCallback(() => {
    setStep('idle');
    setErrorCode(null);
  }, []);

  const finish = useCallback(() => setStep('idle'), []);

  const sendCode = useCallback(() => {
    requestMutation.mutate(newEmail.trim().toLowerCase());
  }, [newEmail, requestMutation]);

  const confirm = useCallback(() => {
    confirmMutation.mutate(code.trim());
  }, [code, confirmMutation]);

  const resend = useCallback(() => {
    if (Date.now() >= resendAvailableAt) {
      requestMutation.mutate(newEmail.trim().toLowerCase());
    }
  }, [newEmail, requestMutation, resendAvailableAt]);

  return {
    step,
    newEmail,
    setNewEmail,
    code,
    setCode,
    errorCode,
    resendInSeconds: Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000)),
    isSending: requestMutation.isPending,
    isConfirming: confirmMutation.isPending,
    start,
    cancel,
    finish,
    sendCode,
    confirm,
    resend,
  };
}
